import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import AXIOS_INSTANCE from '../api/axiosInstance'
import { playNotificationSound } from '../notifications/playNotificationSound'
import type { AppNotification, NotificationAudience, NotificationCategory } from '../notifications/types'
import { createChannelSubscription } from '../realtime/cable'
import { applyClinicUpdate } from '../realtime/clinicUpdate'
import { queryClient } from '../store/provider/TanStackProvider'

type NotificationApiAttrs = {
  category: NotificationCategory
  title: string
  body: string
  action_url?: string | null
  audience: NotificationAudience
  read_at: string | null
  created_at: string
  unread?: boolean
  notifiable_type?: string | null
  notifiable_id?: string | number | null
}

export type MarkReadByNotifiableInput = {
  type: string
  id: string
}

type NotificationResource = {
  id: string
  type: string
  attributes: NotificationApiAttrs
}

type NotificationsIndexResponse = {
  data: NotificationResource[]
  meta?: { unread_count?: number }
}

type NotificationsQueryData = {
  items: AppNotification[]
  unreadCount: number
}

const mapNotification = (row: NotificationResource): AppNotification => ({
  id: row.id,
  category: row.attributes.category,
  title: row.attributes.title,
  body: row.attributes.body,
  actionUrl: row.attributes.action_url,
  audience: row.attributes.audience,
  readAt: row.attributes.read_at,
  createdAt: row.attributes.created_at,
  unread: row.attributes.unread ?? !row.attributes.read_at,
  notifiableType: row.attributes.notifiable_type ?? null,
  notifiableId:
    row.attributes.notifiable_id === null || row.attributes.notifiable_id === undefined
      ? null
      : String(row.attributes.notifiable_id)
})

const markNotifiableReadInCache = (
  data: NotificationsQueryData | undefined,
  type: string,
  id: string,
  readAt: string
) => {
  if (!data) return data
  const items = data.items.map(item =>
    item.notifiableType === type && item.notifiableId === id && !item.readAt ? { ...item, readAt, unread: false } : item
  )
  return {
    items,
    unreadCount: items.filter(i => i.unread).length
  }
}

const NOTIFICATIONS_QUERY_KEY = ['notifications'] as const

const notificationsChannel = createChannelSubscription('NotificationsChannel', 'Notifications')

export const useNotificationsSubscription = (enabled = true) => {
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return

    notificationsChannel.ensure(message => {
      playNotificationSound()
      void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY })
      applyClinicUpdate(message)
      void queryClient.invalidateQueries({ queryKey: ['staffs'] })
      void queryClient.invalidateQueries({ queryKey: ['patients'] })
    })

    return () => {
      notificationsChannel.release()
    }
  }, [enabled])
}

const markItemReadInCache = (data: NotificationsQueryData | undefined, id: string, readAt: string) => {
  if (!data) return data
  const items = data.items.map(item => (item.id === id && !item.readAt ? { ...item, readAt, unread: false } : item))
  return {
    items,
    unreadCount: items.filter(i => i.unread).length
  }
}

const markAllReadInCache = (data: NotificationsQueryData | undefined, readAt: string) => {
  if (!data) return data
  const items = data.items.map(item => (item.readAt ? item : { ...item, readAt, unread: false }))
  return { items, unreadCount: 0 }
}

export const useNotifications = (opts?: { audience?: NotificationAudience; enabled?: boolean }) => {
  const enabled = opts?.enabled !== false
  const audience = opts?.audience
  const queryKey = [...NOTIFICATIONS_QUERY_KEY, { audience }] as const

  useNotificationsSubscription(enabled)

  const { data, isLoading, isError } = useQuery({
    enabled,
    queryFn: async () => {
      const { data: res } = await AXIOS_INSTANCE.get<NotificationsIndexResponse>('notifications', {
        params: { 'page[size]': 30 }
      })
      const items = (res.data || []).map(mapNotification)
      const filtered = audience ? items.filter(i => i.audience === audience) : items
      const sorted = [...filtered].sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt))
      return {
        items: sorted,
        unreadCount: sorted.filter(i => i.unread).length
      }
    },
    queryKey,
    refetchInterval: 60_000
  })

  // Poll / refetch fallback when ActionCable did not deliver (or was unavailable).
  const prevUnreadRef = useRef<number | null>(null)
  useEffect(() => {
    if (!enabled || data == null) return
    const prev = prevUnreadRef.current
    prevUnreadRef.current = data.unreadCount
    if (prev !== null && data.unreadCount > prev) {
      playNotificationSound()
    }
  }, [enabled, data])

  const { mutateAsync: markRead } = useMutation({
    mutationFn: async (id: string) => {
      await AXIOS_INSTANCE.patch(`notifications/${id}/mark_read`)
    },
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_QUERY_KEY })
      const previous = queryClient.getQueriesData<NotificationsQueryData>({ queryKey: NOTIFICATIONS_QUERY_KEY })
      const readAt = new Date().toISOString()
      queryClient.setQueriesData<NotificationsQueryData>({ queryKey: NOTIFICATIONS_QUERY_KEY }, old =>
        markItemReadInCache(old, id, readAt)
      )
      return { previous }
    },
    onError: (_err, _id, context) => {
      context?.previous?.forEach(([key, value]) => {
        queryClient.setQueryData(key, value)
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY })
    }
  })

  const { mutateAsync: markAllRead } = useMutation({
    mutationFn: async () => {
      await AXIOS_INSTANCE.patch('notifications/mark_all_read')
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_QUERY_KEY })
      const previous = queryClient.getQueriesData<NotificationsQueryData>({ queryKey: NOTIFICATIONS_QUERY_KEY })
      const readAt = new Date().toISOString()
      queryClient.setQueriesData<NotificationsQueryData>({ queryKey: NOTIFICATIONS_QUERY_KEY }, old =>
        markAllReadInCache(old, readAt)
      )
      return { previous }
    },
    onError: (_err, _vars, context) => {
      context?.previous?.forEach(([key, value]) => {
        queryClient.setQueryData(key, value)
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY })
    }
  })

  const { mutateAsync: markReadByNotifiable } = useMutation({
    mutationFn: async ({ type, id }: MarkReadByNotifiableInput) => {
      await AXIOS_INSTANCE.patch('notifications/mark_read_by_notifiable', {
        notifiable_type: type,
        notifiable_id: id
      })
    },
    onMutate: async ({ type, id }) => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_QUERY_KEY })
      const previous = queryClient.getQueriesData<NotificationsQueryData>({ queryKey: NOTIFICATIONS_QUERY_KEY })
      const readAt = new Date().toISOString()
      queryClient.setQueriesData<NotificationsQueryData>({ queryKey: NOTIFICATIONS_QUERY_KEY }, old =>
        markNotifiableReadInCache(old, type, id, readAt)
      )
      return { previous }
    },
    onError: (_err, _vars, context) => {
      context?.previous?.forEach(([key, value]) => {
        queryClient.setQueryData(key, value)
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY })
    }
  })

  return {
    items: data?.items || [],
    unreadCount: data?.unreadCount || 0,
    isLoading,
    isError,
    markRead,
    markAllRead,
    markReadByNotifiable
  }
}
